import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { MediaPicker, type MediaPickerValue } from '@/components/media/MediaPicker';
import { api } from '@/lib/api';
import { librarySuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import {
  libraryBackLabel,
  libraryBackUrl,
  libraryShowUrl,
  RETURN_TO_BUILDER,
} from '@/lib/library-navigation';
import {
  INTEGRATION_TYPES,
  LIBRARY_TYPE_OPTIONS,
  MEDIA_FILTER_BY_TYPE,
  TYPES_REQUIRING_CONTENT,
  TYPES_REQUIRING_MEDIA,
  mediaLabelForType,
  slugifyLibrary,
} from './library-form-constants';
import { PdfFolderField, type PdfFolderFile } from './PdfFolderField';

interface LibraryForm {
  title: string;
  slug: string;
  type: string;
  description: string;
  status: string;
  tagsInput: string;
  isVisible: boolean;
  isDownloadable: boolean;
  isPublic: boolean;
  content: string;
  externalUrl: string;
}

interface LibraryItemResponse extends LibraryForm {
  id: string;
  mediaFileId?: string;
  thumbnailMediaId?: string;
  files?: PdfFolderFile[];
}

function TogglePill({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-full border border-dash-outline-variant px-4 py-2 transition-colors hover:bg-secondary-100 has-[:checked]:border-dash-primary has-[:checked]:bg-brand-100 has-[:checked]:text-dash-primary">
      <input type="checkbox" className="sr-only" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm font-medium">{label}</span>
    </label>
  );
}

export function LibraryFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const initialType = searchParams.get('type') ?? 'rich_text';

  const [mediaFile, setMediaFile] = useState<MediaPickerValue | null>(null);
  const [folderFiles, setFolderFiles] = useState<PdfFolderFile[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<MediaPickerValue | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const { data: item, isLoading } = useQuery({
    queryKey: ['library', id],
    queryFn: () => api<LibraryItemResponse>(`/library/${id}`),
    enabled: isEdit,
  });

  const { register, handleSubmit, watch, setValue, reset } = useForm<LibraryForm>({
    defaultValues: {
      title: '',
      slug: '',
      type: initialType,
      description: '',
      status: 'draft',
      tagsInput: '',
      isVisible: true,
      isDownloadable: false,
      isPublic: false,
      content: '',
      externalUrl: '',
    },
  });

  const selectedType = watch('type');
  const title = watch('title');

  useEffect(() => {
    if (!item) return;
    reset({
      title: item.title,
      slug: item.slug,
      type: item.type,
      description: item.description ?? '',
      status: item.status ?? 'draft',
      tagsInput: item.tagsInput ?? '',
      isVisible: item.isVisible !== false,
      isDownloadable: Boolean(item.isDownloadable),
      isPublic: Boolean(item.isPublic),
      content: item.content ?? '',
      externalUrl: item.externalUrl ?? '',
    });
    if (item.mediaFileId) {
      setMediaFile({ id: item.mediaFileId, title: 'Archivo seleccionado' });
    }
    setFolderFiles(item.files ?? []);
    if (item.thumbnailMediaId) {
      setThumbnailFile({ id: item.thumbnailMediaId, title: 'Miniatura seleccionada' });
    }
  }, [item, reset]);

  useEffect(() => {
    if (isEdit || slugTouched) return;
    setValue('slug', slugifyLibrary(title));
  }, [title, isEdit, slugTouched, setValue]);

  const typeLocked = isEdit && INTEGRATION_TYPES.has(item?.type ?? '');
  const hideContentSection = INTEGRATION_TYPES.has(selectedType);
  const mediaFilterType = MEDIA_FILTER_BY_TYPE[selectedType] ?? null;

  const mutation = useMutation({
    mutationFn: (values: LibraryForm) => {
      const body = {
        title: values.title,
        slug: values.slug || undefined,
        type: values.type,
        status: values.status,
        description: values.description,
        content: values.content,
        externalUrl: values.externalUrl || null,
        mediaFileId: values.type === 'pdf_folder' ? null : mediaFile?.id ?? null,
        files: values.type === 'pdf_folder' ? folderFiles : undefined,
        thumbnailMediaId: thumbnailFile?.id ?? null,
        tagsInput: values.tagsInput,
        isVisible: values.isVisible,
        isDownloadable: values.isDownloadable,
        isPublic: values.isPublic,
      };
      return isEdit
        ? api<{ id: string }>(`/library/${id}`, { method: 'PATCH', body })
        : api<{ id: string }>('/library', { method: 'POST', body });
    },
    onSuccess: (result: { id: string }) => {
      toast(librarySuccessToast(isEdit ? 'edit' : 'create'));
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      if (isEdit && id) void queryClient.invalidateQueries({ queryKey: ['library', id] });
      if (searchParams.get('return_to') === RETURN_TO_BUILDER && searchParams.get('course')) {
        navigate(libraryBackUrl(searchParams));
        return;
      }
      const objectId = result.id ?? id;
      if (!objectId) return;
      navigate(libraryShowUrl(objectId, searchParams));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, isEdit ? 'No se pudo guardar el objeto' : 'No se pudo crear el objeto'));
    },
  });

  const backTo = libraryBackUrl(searchParams);

  const typeOptions = useMemo(() => {
    if (isEdit && item && INTEGRATION_TYPES.has(item.type)) {
      const existing = LIBRARY_TYPE_OPTIONS.filter((opt) => opt.value === item.type);
      if (existing.length > 0) return existing;
      const fallbackLabels: Record<string, string> = {
        h5p: 'H5P',
        assessment: 'Evaluación',
      };
      return [{ value: item.type, label: fallbackLabels[item.type] ?? item.type }];
    }
    return LIBRARY_TYPE_OPTIONS;
  }, [isEdit, item]);

  return (
    <div className="lms-library-form lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to={backTo} label={libraryBackLabel(searchParams)} />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar objeto de aprendizaje' : 'Nuevo objeto de aprendizaje'}
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {isEdit ? item?.title : 'El contenido se almacena una sola vez en la biblioteca central'}
          </p>
        </div>
      </header>

      {!isEdit && (
        <div className="mb-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="lms-library-form__banner lms-library-form__banner--h5p">
            <span className="material-symbols-outlined text-3xl text-[#0060ac]" aria-hidden="true">
              extension
            </span>
            <div>
              <h3 className="text-sm font-bold text-[#001c39]">¿Buscas crear una actividad interactiva H5P?</h3>
              <p className="mt-1 text-base leading-relaxed text-[#001c39]/90">
                Usa la{' '}
                <Link to="/h5p/create" className="font-semibold underline hover:opacity-80">
                  Biblioteca H5P
                </Link>{' '}
                para importar el paquete y registrar automáticamente sus librerías y dependencias.
              </p>
            </div>
          </div>
          <div className="lms-library-form__banner lms-library-form__banner--assessment">
            <span className="material-symbols-outlined text-3xl text-[#292d2f]" aria-hidden="true">
              quiz
            </span>
            <div>
              <h3 className="text-sm font-bold text-[#181c1e]">¿Buscas crear una evaluación?</h3>
              <p className="mt-1 text-base leading-relaxed text-[#181c1e]/90">
                Usa el{' '}
                <Link to="/evaluaciones/create" className="font-semibold underline hover:opacity-80">
                  Motor de Evaluaciones
                </Link>{' '}
                con banco de preguntas reutilizable y versionado.
              </p>
            </div>
          </div>
        </div>
      )}

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <form
          onSubmit={handleSubmit((values) => {
            if (values.type === 'pdf_folder' && folderFiles.length === 0) {
              toast({ title: 'Agrega al menos un PDF a la carpeta.', variant: 'danger' });
              return;
            }
            mutation.mutate(values);
          })}
          className="space-y-8"
        >
          <section className="lms-library-form__card">
            <h2 className="lms-library-form__card-title">
              <span className="material-symbols-outlined" aria-hidden="true">
                info
              </span>
              Datos Generales
            </h2>

            <div className="lms-library-form__grid">
              <div className="lms-library-form__field">
                <label className="lms-library-form__label" htmlFor="library-name">
                  Nombre (Requerido)
                </label>
                <input
                  id="library-name"
                  type="text"
                  required
                  placeholder="Ej: Introducción a la Termodinámica"
                  className="lms-library-form__input"
                  {...register('title', { required: true })}
                />
              </div>

              <div className="lms-library-form__field">
                {typeLocked ? (
                  <>
                    <span className="lms-library-form__label">Tipo</span>
                    <p className="lms-library-form__input lms-library-form__input--readonly">
                      {LIBRARY_TYPE_OPTIONS.find((opt) => opt.value === item?.type)?.label ?? item?.type}
                    </p>
                  </>
                ) : (
                  <>
                    <label className="lms-library-form__label" htmlFor="library-type">
                      Tipo
                    </label>
                    <select
                      id="library-type"
                      required
                      className="lms-library-form__input lms-library-form__select"
                      {...register('type', { required: true })}
                    >
                      {typeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>

              <div className="lms-library-form__field lms-library-form__field--full">
                <label className="lms-library-form__label" htmlFor="library-description">
                  Descripción
                </label>
                <textarea
                  id="library-description"
                  rows={4}
                  placeholder="Breve resumen del contenido..."
                  className="lms-library-form__input lms-library-form__textarea"
                  {...register('description')}
                />
              </div>

              <div className="lms-library-form__field">
                <label className="lms-library-form__label" htmlFor="library-status">
                  Estado
                </label>
                <select id="library-status" className="lms-library-form__input lms-library-form__select" {...register('status')}>
                  <option value="draft">Borrador</option>
                  <option value="published">Publicado</option>
                  <option value="archived">Archivado</option>
                </select>
              </div>

              <div className="lms-library-form__field">
                <label className="lms-library-form__label" htmlFor="library-tags">
                  Etiquetas
                </label>
                <input
                  id="library-tags"
                  type="text"
                  placeholder="Etiqueta1, Etiqueta2..."
                  className="lms-library-form__input"
                  {...register('tagsInput')}
                />
              </div>

              <div className="lms-library-form__field">
                <label className="lms-library-form__label" htmlFor="library-slug">
                  Slug
                </label>
                <input
                  id="library-slug"
                  type="text"
                  className="lms-library-form__input"
                  {...register('slug')}
                  onChange={(event) => {
                    setSlugTouched(true);
                    setValue('slug', event.target.value);
                  }}
                />
              </div>

              <div className="lms-library-form__field lms-library-form__field--full">
                <div className="flex flex-wrap gap-3 py-4">
                  <TogglePill label="Visible" checked={watch('isVisible')} onChange={(v) => setValue('isVisible', v)} />
                  <TogglePill
                    label="Descargable"
                    checked={watch('isDownloadable')}
                    onChange={(v) => setValue('isDownloadable', v)}
                  />
                  <TogglePill label="Público" checked={watch('isPublic')} onChange={(v) => setValue('isPublic', v)} />
                </div>
              </div>
            </div>
          </section>

          {!hideContentSection && (
            <section className="lms-library-form__card">
              <h2 className="lms-library-form__card-title lms-library-form__card-title--tight">
                <span className="material-symbols-outlined" aria-hidden="true">
                  perm_media
                </span>
                Contenido desde Biblioteca Multimedia
              </h2>
              <p className="lms-library-form__hint mb-8 max-w-3xl">
                {selectedType === 'pdf_folder'
                  ? 'Agrega uno o más PDF desde Multimedia. El orden de la lista es el orden en que se verán en el curso.'
                  : 'Los archivos deben existir en Multimedia. Puede subir un archivo nuevo (se guardará en la biblioteca) o elegir uno existente. Para video, PDF, audio e imagen debe seleccionar un archivo o indicar un enlace externo.'}
              </p>

              <div className="space-y-8">
                {selectedType === 'pdf_folder' ? (
                  <PdfFolderField files={folderFiles} onChange={setFolderFiles} />
                ) : (
                <>
                <div className="lms-library-form__dropzone">
                  <span className="material-symbols-outlined lms-library-form__dropzone-icon" aria-hidden="true">
                    cloud_upload
                  </span>
                  <MediaPicker
                    label={mediaLabelForType(selectedType)}
                    value={mediaFile}
                    onChange={setMediaFile}
                    filterType={mediaFilterType}
                  />
                </div>

                <div className="lms-library-form__field">
                  <label className="lms-library-form__label" htmlFor="library-content">
                    Texto / HTML {TYPES_REQUIRING_CONTENT.has(selectedType) ? '(requerido)' : '(si aplica)'}
                  </label>
                  <div className="lms-library-form__editor">
                    <div className="lms-library-form__editor-toolbar" aria-hidden="true">
                      <span className="material-symbols-outlined text-sm">format_bold</span>
                      <span className="material-symbols-outlined text-sm">format_italic</span>
                      <span className="material-symbols-outlined text-sm">format_list_bulleted</span>
                      <span className="material-symbols-outlined text-sm">link</span>
                      <span className="material-symbols-outlined text-sm">code</span>
                    </div>
                    <textarea
                      id="library-content"
                      rows={10}
                      placeholder="Escriba o pegue su contenido HTML aquí..."
                      className="lms-library-form__editor-area"
                      {...register('content', {
                        validate: (value) =>
                          !TYPES_REQUIRING_CONTENT.has(selectedType) || value.trim()
                            ? true
                            : 'El contenido de texto es obligatorio para este tipo.',
                      })}
                    />
                  </div>
                </div>

                <div className="lms-library-form__field">
                  <label className="lms-library-form__label" htmlFor="library-external-url">
                    Enlace externo {selectedType === 'external_link' || TYPES_REQUIRING_MEDIA.has(selectedType) ? '(si aplica)' : ''}
                  </label>
                  <div className="relative">
                    <span
                      className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#454651]"
                      aria-hidden="true"
                    >
                      link
                    </span>
                    <input
                      id="library-external-url"
                      type="url"
                      placeholder="https://youtube.com/embed/..."
                      className="lms-library-form__input lms-library-form__input--with-icon"
                      {...register('externalUrl', {
                        validate: (value) =>
                          selectedType !== 'external_link' || value.trim()
                            ? true
                            : 'El enlace externo es obligatorio.',
                      })}
                    />
                  </div>
                </div>
                </>
                )}
              </div>
            </section>
          )}

          <section className="lms-library-form__card">
            <h2 className="lms-library-form__card-title lms-library-form__card-title--tight">
              <span className="material-symbols-outlined" aria-hidden="true">
                image
              </span>
              Miniatura (desde Multimedia)
            </h2>
            <div className="lms-library-form__thumb-row">
              <div className="lms-library-form__thumb-preview" aria-hidden="true">
                <span className="material-symbols-outlined text-4xl text-[#767682]">image</span>
              </div>
              <div className="lms-library-form__thumb-picker min-w-0 flex-1">
                <p className="lms-library-form__hint mb-4">
                  Selecciona un archivo de la Biblioteca Multimedia o súbelo aquí para identificar visualmente este
                  objeto.
                </p>
                <MediaPicker label="" value={thumbnailFile} onChange={setThumbnailFile} filterType="image" />
              </div>
            </div>
          </section>

          <footer className="lms-library-form__footer">
            <Link to={backTo} className="lms-library-form__cancel">
              Cancelar
            </Link>
            <button type="submit" className="lms-library-form__submit" disabled={mutation.isPending}>
              {isEdit ? 'Guardar cambios' : 'Crear objeto'}
            </button>
          </footer>
        </form>
      )}
    </div>
  );
}
